import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import * as authActions from '../redux/actions/authActions';
import Form from '../components/signinForm/signinForm';
import Snackbar from '../components/Snackbar';
import { withRouter } from 'next/router';
import withAlreadyAuth from '../hoc/withAlreadyAuth';
import 'react-intl-tel-input/dist/main.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { datadogLogs } from '@datadog/browser-logs';
// Canvas Fingerprinting Functions
const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 280;
    canvas.height = 60;
    
    // Draw background
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, 280, 60);
    
    // Draw text with various styles
    ctx.fillStyle = '#222';
    ctx.font = '18px Arial';
    ctx.fillText('Canvas fingerprint 🚀', 10, 20);
    
    // Add more complexity for uniqueness
    ctx.font = '14px Times New Roman';
    ctx.fillText('Special chars: @#$%^&*()', 10, 35);
    
    // Draw geometric shapes
    ctx.beginPath();
    ctx.moveTo(170, 10);
    ctx.lineTo(180, 25);
    ctx.lineTo(160, 25);
    ctx.closePath();
    ctx.fillStyle = 'rgb(255, 0, 0)';
    ctx.fill();
    
    // Draw circle with gradient
    const gradient = ctx.createRadialGradient(200, 30, 0, 200, 30, 20);
    gradient.addColorStop(0, 'blue');
    gradient.addColorStop(1, 'white');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(200, 30, 15, 0, Math.PI * 2, true);
    ctx.fill();
    
    // Add noise pattern
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    for (let i = 0; i < 30; i++) {
      ctx.fillRect(Math.random() * 280, Math.random() * 60, 2, 2);
    }
    
    // Draw complex Unicode characters
    ctx.font = '16px Arial';
    ctx.fillStyle = '#333';
    ctx.fillText('🔒🔑📱💻', 10, 55);
    
    // Convert to data URL
    const dataURL = canvas.toDataURL('image/png');
    
    // Also get raw pixel data for additional entropy
    const imageData = ctx.getImageData(0, 0, 280, 60);
    let pixelHash = 0;
    
    // Sample specific pixels for fingerprinting
    const samplePoints = [
      [10, 10], [50, 20], [100, 30], [150, 40], [200, 50],
      [30, 15], [80, 25], [130, 35], [180, 45], [230, 55]
    ];
    
    for (const [x, y] of samplePoints) {
      const index = (y * 280 + x) * 4;
      pixelHash += imageData.data[index] * 1000000;     // R
      pixelHash += imageData.data[index + 1] * 1000;    // G
      pixelHash += imageData.data[index + 2];           // B
    }
    
    return {
      dataURL: dataURL,
      pixelHash: pixelHash,
      hash: dataURL
    };
  } catch (error) {
    console.error('Canvas fingerprinting failed:', error);
    datadogLogs.logger.error('Canvas fingerprinting failed', error);
    return null;
  }
};

// WebGL Canvas Fingerprinting
const getWebGLCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) {
      return null;
    }
    
    // Vertex shader
    const vertexShaderSource = `
      attribute vec2 attrVertex;
      varying vec2 varyingTextureCoord;
      uniform vec2 uniformOffset;
      void main() {
        varyingTextureCoord = attrVertex + uniformOffset;
        gl_Position = vec4(attrVertex, 0, 1);
      }
    `;
    
    // Fragment shader with gradient
    const fragmentShaderSource = `
      precision mediump float;
      varying vec2 varyingTextureCoord;
      void main() {
        gl_FragColor = vec4(varyingTextureCoord, 0, 1);
      }
    `;
    
    // Create and compile shaders
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);
    
    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);
    
    // Create program
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);
    
    // Create vertex buffer
    const vertexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -0.5, -0.5,
       0.5, -0.5,
       0.0,  0.5
    ]), gl.STATIC_DRAW);
    
    // Set attributes and uniforms
    const attrVertex = gl.getAttribLocation(program, 'attrVertex');
    gl.enableVertexAttribArray(attrVertex);
    gl.vertexAttribPointer(attrVertex, 2, gl.FLOAT, false, 0, 0);
    
    const uniformOffset = gl.getUniformLocation(program, 'uniformOffset');
    gl.uniform2f(uniformOffset, 1, 1);
    
    // Draw
    gl.clearColor(0.0, 0.0, 0.0, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    
    // Get canvas data
    const webglCanvas = gl.canvas;
    const webglDataURL = webglCanvas.toDataURL();
    
    // Get WebGL parameters for additional entropy
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'Unknown';
    const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'Unknown';
    
    return {
      dataURL: webglDataURL,
      vendor: vendor,
      renderer: renderer,
      hash: hashCanvas(webglDataURL) // Apply consistent hashing
    };
  } catch (error) {
    console.error('WebGL fingerprinting failed:', error);
    datadogLogs.logger.error('WebGL fingerprinting failed', error);
    return null;
  }
};

// Hash function for canvas data
const hashCanvas = (dataURL) => {
  let hash = 0;
  for (let i = 0; i < dataURL.length; i++) {
    const char = dataURL.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
};

// Combined fingerprinting function
const getCompleteFingerprint = async () => {
  try {
    const fingerprints = {
      canvas: getCanvasFingerprint(),
      webgl: getWebGLCanvasFingerprint(),
      
      // Additional browser fingerprints
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        orientation: screen.orientation?.type
      },
      
      // Navigator properties
      navigator: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages,
        platform: navigator.platform,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: navigator.deviceMemory,
        maxTouchPoints: navigator.maxTouchPoints,
        vendor: navigator.vendor,
        appVersion: navigator.appVersion,
        product: navigator.product,
        productSub: navigator.productSub,
        vendorSub: navigator.vendorSub,
        doNotTrack: navigator.doNotTrack,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine
      }
    };
    
    // Create a composite hash from all fingerprints
    const compositeString = JSON.stringify(fingerprints);
    const compositeHash = compositeString;
    
    return {
      fingerprints: fingerprints,
      compositeHash: compositeHash,
      // Use the most reliable identifiers
      deviceId: `${fingerprints.canvas?.hash || ''}_${fingerprints.webgl?.hash || ''}_${compositeHash}`
    };
  } catch (error) {
    console.error('Complete fingerprint generation failed:', error);
    datadogLogs.logger.error('Complete fingerprint generation failed', error);
    return {
      fingerprints: { canvas: null, webgl: null, screen: {}, navigator: {} },
      compositeHash: 'fallback-hash',
      deviceId: 'fallback-device-id'
    };
  }
};

function SendLoginCode(props) {
  const [showSnack, setShowSnack] = useState(false);
  const [milliseconds, setMilliseconds] = useState(0);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [honeypotPhone, setHoneypotPhone] = useState('');
  const [isGeneratingHash, setIsGeneratingHash] = useState(false);
  const recaptchaRef = useRef();
  const recaptchaCheckboxRef = useRef();
  
  const [isCheckboxVerified, setIsCheckboxVerified] = useState(false);
  const [checkboxToken, setCheckboxToken] = useState(null);
  
  const isStageEnv = process.env.NEXT_PUBLIC_CAPTCHA_PUBLIC_DISABLED === 'true';
  
  // Generate device fingerprint on component mount
  useEffect(() => {
    const generateFingerprint = async () => {
      try {
        const fingerprint = await getCompleteFingerprint();
        setDeviceFingerprint(fingerprint);
      } catch (error) {
        console.error('Error generating device fingerprint:', error);
        datadogLogs.logger.error('Device fingerprint generation failed', error);
      }
    };
    
    generateFingerprint();
  }, []);
  
  const onCheckboxChange = (token) => {
    setCheckboxToken(token);
    setIsCheckboxVerified(!!token);
  };
  const onSubmit = async (values) => {
    try {
      setIsGeneratingHash(true);
      
      const coreData = {
        phone: values.phone,
        country_code: values.country_code,
      };

      // Generate hash for ONLY these core fields
      const hashResponse = await fetch('/api/generate-hash', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(coreData)
      });

      if (!hashResponse.ok) {
        throw new Error('حدث خطأ ما');
      }

      const { hash: secureHash, coreData: serverCoreData } = await hashResponse.json();

      // Log to Datadog
      datadogLogs.logger.info('Login attempt', {
        phone: values.phone,
        country_code: values.country_code,
        webgl_data_url_hash: hashCanvas(deviceFingerprint?.fingerprints.webgl?.dataURL || ''),
        canvas_data_url_hash: hashCanvas(deviceFingerprint?.fingerprints.canvas?.dataURL || ''),
        webgl_vendor: deviceFingerprint?.fingerprints.webgl?.vendor || null,
        webgl_renderer: deviceFingerprint?.fingerprints.webgl?.renderer || null,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        platform: navigator.platform
      });

      const payload = {
        ...values,
        'g-x-t': milliseconds / 1000,
        'reference-id': honeypotPhone,
        'timestamp': serverCoreData.timestamp,
        'device-id': deviceFingerprint?.deviceId || null,
        'canvas-hash': deviceFingerprint?.fingerprints.canvas?.hash || null,
        'webgl-hash': deviceFingerprint?.fingerprints.webgl?.hash || null,
        deviceInfo: deviceFingerprint ? {
          screen: deviceFingerprint.fingerprints.screen,
          navigator: {
            userAgent: deviceFingerprint.fingerprints.navigator.userAgent,
            language: deviceFingerprint.fingerprints.navigator.language,
            platform: deviceFingerprint.fingerprints.navigator.platform
          }
        } : null
      };

      if (isStageEnv) {
        props.sendLoginCode({
          ...payload,
          'g-token': 'g-token-staging',
        }, secureHash);
      } else {
        if (!isCheckboxVerified) {
          alert('يرجى إكمال التحقق من "لست روبوت" أولاً');
          return;
        }
        const invisibleToken = await recaptchaRef.current.executeAsync();
        
        props.sendLoginCode({
          ...payload,
          'g-token': invisibleToken, 
          'g-token-checkbox': checkboxToken,   
        }, secureHash);
      }
    } catch (error) {
      setIsGeneratingHash(false);
      
      // Log error to Datadog with context
      datadogLogs.logger.error('SendLoginCode Error', {
        error: error?.message || error || 'Unknown Error',
        phone: values?.phone || null,
        webgl_hash: deviceFingerprint?.fingerprints.webgl?.hash || null,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsGeneratingHash(false);
      setHoneypotPhone('');
    }
  };

  React.useEffect(() => {
    if (props.completed === 'SEND_CODE') {
      props.router.push('/verify_phone', '/verify_phone');
    }
    if (props.error?.type === 'SEND_CODE') {
      setShowSnack(true);
    }
  }, [props.completed, props.error]);

  useEffect(() => {
    const timer = setInterval(() => {
      setMilliseconds(milliseconds + 1);
    }, 1);
    return () => clearInterval(timer);
  });

  return (
    <main style={{ height: '72vh' }}>
      <input
        id="tel-input"
        name="tel-input"
        type="tel"
        value={honeypotPhone}
        onChange={(e) => setHoneypotPhone(e.target.value)}
        autoComplete="off"
        tabIndex={-1}
        style={{
          position: 'absolute',
          left: '-10000px',
          top: '-10000px',
          width: '0',
          height: '0',
          opacity: 0,
          border: 'none',
          pointerEvents: 'none',
          outline: 'none',
          padding: '0',
          margin: '0',
          fontSize: '0',
          lineHeight: '0',
          zIndex: '-1',
        }}
        className='tel-input'
      />
      <Snackbar error show={showSnack} message={props.error?.message} />
      <div className="#login">
        <div className="container">
          <div className="page-top-bar d-flex justify-content-start"></div>
          <div className="form-container login">
            <div className="row" style={{ marginBottom: 10, direction: 'rtl' }}>
              <div className="col-md-3" />
              <h1 className="page-login-title col-md-3">تسجيل دخول </h1>
            </div>
            <Form
              onProgress={props.onProgress === 'SEND_CODE' || isGeneratingHash}
              onSubmit={onSubmit}
              disabled={!isCheckboxVerified || isGeneratingHash}
              isGeneratingHash={isGeneratingHash}
              {...props}
            />
              <>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.NEXT_PUBLIC_CAPTCHA_PUBLIC_KEY}
                />
                
                <div style={{ 
                  marginTop: 20, 
                  marginBottom: 20, 
                  display: 'flex', 
                  justifyContent: 'center',
                  direction: 'ltr'
                }}>
                  <ReCAPTCHA
                    ref={recaptchaCheckboxRef}
                    sitekey={process.env.NEXT_PUBLIC_CAPTCHA_CHECKBOX_SITE_KEY}
                    onChange={onCheckboxChange}
                    onExpired={() => {
                      setIsCheckboxVerified(false);
                      setCheckboxToken(null);
                    }}
                    onError={() => {
                      setIsCheckboxVerified(false);
                      setCheckboxToken(null);
                    }}
                    hl='ar'
                  />
                </div>
              </>
          </div>
        </div>
      </div>
      <style scoped>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
}

const mapDispatchToProps = {
  sendLoginCode: authActions.sendLoginCode,
  clear: authActions.clearMessages,
  resetPhone: authActions.resetPhone,
  verifyPhone: authActions.verifyPhone,
};

const mapStateToProps = (state) => ({
  onProgress: state.auth?.onProgress,
  completed: state.auth?.completed,
  error: state.auth?.error,
  isNew: state.auth?.isNew,
  isVerified: state.auth?.isVerified,
  currentUser: state.auth?.currentUser,
});

export default withAlreadyAuth(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(SendLoginCode))
);